import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Pencil, Trash, Plus, X, PlayCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";
import { youTubeThumb } from "../../lib/youtube";

const empty = {
  title: { en: "", gr: "" },
  description: { en: "", gr: "" },
  video_url: "",
  thumbnail: "",
  duration: "",
  author: "MP Sound Team",
  tags: [],
  published: true,
};

export default function AdminVlogs() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/vlogs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      tags: typeof editing.tags === "string" ? editing.tags.split(",").map(t => t.trim()).filter(Boolean) : (editing.tags || []),
    };
    try {
      if (editing.id) await api.put(`/admin/vlogs/${editing.id}`, payload);
      else await api.post("/admin/vlogs", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this vlog?")) return;
    await api.delete(`/admin/vlogs/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-vlogs">
      <div className="flex items-center justify-between mb-8">
        <h1 className="display-font text-3xl font-black uppercase">Vlogs</h1>
        <button onClick={() => setEditing({ ...empty })} className="btn-primary" data-testid="new-vlog-btn"><Plus size={18} weight="bold" /> New</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((v) => {
          const thumb = v.thumbnail || youTubeThumb(v.video_url);
          return (
            <div key={v.id} className="card-dark overflow-hidden" data-testid={`admin-vlog-${v.id}`}>
              <div className="relative aspect-video bg-[#080809]">
                {thumb && <img src={thumb} alt="" className="w-full h-full object-cover" />}
                <div className="absolute inset-0 flex items-center justify-center"><PlayCircle size={36} weight="fill" className="text-[#ccff00]/90" /></div>
                {v.duration && <span className="absolute bottom-2 right-2 mono-font text-xs bg-black/80 px-2 py-0.5">{v.duration}</span>}
              </div>
              <div className="p-4">
                <div className="display-font font-bold leading-tight">{v.title.en || v.title.gr}</div>
                <div className="mono-font text-xs text-gray-500 mt-1 truncate">/{v.slug} · {v.published ? "published" : "draft"}</div>
                <div className="flex justify-end gap-2 mt-3">
                  <button onClick={() => setEditing({ ...v, tags: (v.tags || []).join(", ") })} className="p-2 hover:text-[#ccff00]"><Pencil size={16} /></button>
                  <button onClick={() => del(v.id)} className="p-2 hover:text-[#ff003c]"><Trash size={16} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-[#0a0a0c] border border-[#2a2a35] max-w-3xl w-full max-h-[90vh] overflow-auto p-6">
            <div className="flex justify-between items-center mb-5">
              <h2 className="display-font text-2xl font-bold">{editing.id ? "Edit" : "New"} vlog</h2>
              <button type="button" onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input required placeholder="Title (EN)" className="input-dark" value={editing.title.en} onChange={(e) => setEditing({ ...editing, title: { ...editing.title, en: e.target.value } })} />
              <input placeholder="Title (GR)" className="input-dark" value={editing.title.gr} onChange={(e) => setEditing({ ...editing, title: { ...editing.title, gr: e.target.value } })} />
              <input required placeholder="YouTube URL or video ID" className="input-dark sm:col-span-2" value={editing.video_url} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} data-testid="vlog-video-url" />
              <div className="sm:col-span-2">
                <ImageUpload value={editing.thumbnail} onChange={(url) => setEditing({ ...editing, thumbnail: url })} label="Custom thumbnail (optional — leave empty for auto YouTube thumb)" />
              </div>
              <input placeholder="Duration (e.g. 3:12)" className="input-dark" value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} />
              <input placeholder="Author" className="input-dark" value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} />
              <input placeholder="Tags (comma separated)" className="input-dark sm:col-span-2" value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} />
              <textarea placeholder="Description (EN)" className="input-dark sm:col-span-2 min-h-[100px]" value={editing.description.en} onChange={(e) => setEditing({ ...editing, description: { ...editing.description, en: e.target.value } })} />
              <textarea placeholder="Description (GR)" className="input-dark sm:col-span-2 min-h-[100px]" value={editing.description.gr} onChange={(e) => setEditing({ ...editing, description: { ...editing.description, gr: e.target.value } })} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published</label>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" className="btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" className="btn-primary">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
